import * as React from "react";
import { useDataProvider, useLocaleState, useNotify } from "react-admin";
import CreatorPage from "../creatorsShared/CreatorPage";
import { createWithId } from "../creatorsShared/createWithId";
import { updateRecord } from "../creatorsShared/updateRecord";
import PreviewBuilderForm from "./PreviewBuilderForm";
import PreviewRender from "./PreviewRender";
import { Service, Translatable, WithId } from "../../types";

interface ServiceOption extends WithId {
  serviceName?: Translatable<string>;
}

interface EditingIds {
  serviceId: string;
  bannerId: string;
}

const emptyDefaults = {
  service: {},
  banner: {},
  forms: [],
};

const PreviewPage = () => {
  const dataProvider = useDataProvider();
  const [locale] = useLocaleState();
  const notify = useNotify();

  const loadRecord = async (option: ServiceOption) => {
    const { data: service } = await dataProvider.getOne<Service>("services", {
      id: option.id,
    });
    // dataProvider.getOne overwrites "id" with the (nonexistent) "_id"
    // field, since the API already renames _id to id on the wire - see
    // src/dataProvider.ts. Every *Edit page gets away with this because
    // it takes the id from the route, not the fetched record. We don't
    // have a route here, so we keep the real ids we already know
    // (option.id, service.banner, each item.form) instead of trusting
    // the fetched records' "id" field.
    const bannerId = service.banner;
    const formRefs = service.forms || [];
    const [{ data: banner }, formsResults] = await Promise.all([
      dataProvider.getOne("banners", { id: bannerId }),
      Promise.all(
        formRefs.map((item) => dataProvider.getOne("forms", { id: item.form }))
      ),
    ]);

    return {
      defaultValues: {
        service: { ...service },
        banner: { ...banner },
        forms: formsResults.map(({ data }, index) => ({
          ...data,
          formId: formRefs[index].form,
        })),
      },
      editingIds: { serviceId: option.id, bannerId },
    };
  };

  const saveRecord = async (
    values: Record<string, any>,
    editingIds: EditingIds | null
  ) => {
    const isEditing = !!editingIds;
    let createdBannerId: string | undefined;
    const createdFormIds: string[] = [];

    const bannerPayload = {
      bannerImage: values.banner?.bannerImage,
      bannerTitle: values.banner?.bannerTitle,
      bannerSubtitle: values.banner?.bannerSubtitle,
      bannerSlogans: values.banner?.bannerSlogans || [],
    };

    let bannerId: string;
    const formIds: string[] = [];
    try {
      if (isEditing) {
        await updateRecord(dataProvider, "banners", editingIds.bannerId, bannerPayload);
        bannerId = editingIds.bannerId;
      } else {
        bannerId = await createWithId("banners", bannerPayload);
        createdBannerId = bannerId;
      }

      for (const formItem of values.forms || []) {
        const { formId, ...formPayload } = formItem || {};
        if (formId) {
          await updateRecord(dataProvider, "forms", formId, formPayload);
          formIds.push(formId);
        } else {
          const newFormId = await createWithId("forms", formPayload);
          formIds.push(newFormId);
          createdFormIds.push(newFormId);
        }
      }
    } catch (error) {
      if (!isEditing && createdBannerId) {
        notify("preview.notifications.partial", {
          type: "warning",
          messageArgs: {
            banner: createdBannerId,
            forms: createdFormIds.join(", ") || "-",
          },
        });
      }
      throw error;
    }

    const servicePayload = {
      cover: values.service?.cover,
      banner: bannerId,
      forms: formIds.map((id) => ({ form: id })),
      serviceName: values.service?.serviceName,
      coverTitle: values.service?.coverTitle,
      coverSubTitle: values.service?.coverSubTitle,
      mainPriceTitle: values.service?.mainPriceTitle,
      mainPriceSubtitle: values.service?.mainPriceSubtitle,
    };

    let serviceId: string;
    if (isEditing) {
      await updateRecord(dataProvider, "services", editingIds.serviceId, servicePayload);
      serviceId = editingIds.serviceId;
    } else {
      serviceId = await createWithId("services", servicePayload);
    }

    return { resource: "services", id: serviceId };
  };

  return (
    <CreatorPage<ServiceOption, EditingIds>
      titleKey="preview.title"
      subtitleKey="preview.subtitle"
      saveKey="preview.save"
      saveExistingKey="preview.saveExisting"
      successKey="preview.notifications.success"
      errorKey="preview.notifications.error"
      loadErrorKey="preview.notifications.loadError"
      selector={{
        resource: "services",
        getOptionLabel: (option) => option.serviceName?.[locale],
        labelKey: "preview.selector.label",
        placeholderKey: "preview.selector.new",
      }}
      emptyDefaults={emptyDefaults}
      loadRecord={loadRecord}
      saveRecord={saveRecord}
      BuilderForm={PreviewBuilderForm}
      RenderPreview={PreviewRender}
    />
  );
};

export default PreviewPage;
